import { useCallback, useEffect, useRef } from "react";
import { useSocket } from "../../Context/SocketContext";

export const useSocketEvents = () => {
  const { socket, isConnected } = useSocket();
  const listenersRef = useRef(new Map());

  // Clean up all listeners on unmount
  useEffect(() => {
    return () => {
      listenersRef.current.forEach((callback, eventName) => {
        socket?.off(eventName, callback);
      });
      listenersRef.current.clear();
    };
  }, [socket]);

  const on = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName || !handler) {
        console.warn("Invalid parameters for socket.on");
        return () => {};
      }

      // Create a wrapper that preserves the latest state
      const wrappedHandler = (...args) => {
        handler(...args);
      };

      // Store reference to the wrapper for cleanup
      listenersRef.current.set(eventName, wrappedHandler);

      // Attach listener
      socket.on(eventName, wrappedHandler);

      // Return cleanup function
      return () => {
        socket.off(eventName, wrappedHandler);
        listenersRef.current.delete(eventName);
      };
    },
    [socket]
  );

  const off = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName) return;

      if (handler) {
        socket.off(eventName, handler);
        listenersRef.current.delete(eventName);
      } else {
        // Remove all listeners for this event
        socket.off(eventName);
        listenersRef.current.delete(eventName);
      }
    },
    [socket]
  );

  const emit = useCallback(
    (eventName, data, callback) => {
      if (!socket || !isConnected) {
        console.warn(`Cannot emit ${eventName}: Socket not connected`);
        return false;
      }

      return socket.emit(eventName, data, callback);
    },
    [socket, isConnected]
  );

  const once = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName || !handler) {
        console.warn("Invalid parameters for socket.once");
        return () => {};
      }

      const wrappedHandler = (...args) => {
        handler(...args);
        listenersRef.current.delete(eventName);
      };

      listenersRef.current.set(eventName, wrappedHandler);
      socket.socket?.once(eventName, wrappedHandler);

      return () => {
        socket.off(eventName, wrappedHandler);
        listenersRef.current.delete(eventName);
      };
    },
    [socket]
  );

  return {
    on,
    off,
    emit,
    once,
    isConnected,
    socketId: socket?.getSocketId(),
  };
};
